import config from '@/config';
import {
  CAROUSEL_EPISODES_LIMIT,
  ComplianceTypeToApiQueryParam,
} from '@/constants';
import {
  ComplianceType,
  DateRange,
  EpisodeFeedback,
  ListenerDataProps,
  Region,
  SubmitProductFeedbackPayload,
} from '@/types';
import {
  getFacetFilters,
  getRegionApiQueryParams,
  getSortByQuery,
  snakeCaseKeys,
} from '@/utils';

import {
  EPISODE_CARD_REQUIRED_FIELDS,
  EPISODE_RELEVANCE_FIELDS,
  PostgrestEndpoints,
} from './postgrest.constants';
import {
  AddMembersPayload,
  AnswerQuizPayload,
  CancelStripeSubscriptionPayload,
  ClaimPlayerSessionPayload,
  EditMembersPayload,
  EpisodeRequestBase,
  FetchCategoryEpisodesParams,
  FetchEpisodeParams,
  FetchEpisodeQuizzesParams,
  FetchEpisodesParams,
  FetchLibraryEpisodesParams,
  FetchNewEpisodesParams,
  FetchUserAssessmentParams,
  PauseSessionParams,
  PlaySessionParams,
  PostSeekPositionParams,
  PostSetPositionParams,
  SubmitUserAssessmentPayload,
} from './postgrest.types';
import { convertToPostgrestDateRange } from './postgrest.utils';

export const refreshAccessTokenRequest = ({
  refreshToken,
}: {
  refreshToken: string;
}) => ({
  url: PostgrestEndpoints.REFRESH_ACCESS_TOKEN,
  method: 'POST',
  body: {
    refresh_token: refreshToken,
  },
});

export const fetchFeaturedEpisodesRequest = ({
  compliance,
  audienceRegions,
  applyRelevanceBoost,
}: EpisodeRequestBase) => ({
  url: PostgrestEndpoints.FEATURED_EPISODES,
  params: {
    region: `eq.${ComplianceTypeToApiQueryParam[compliance]}`,
    is_archived: 'is.false',
    select: [...EPISODE_CARD_REQUIRED_FIELDS, ...EPISODE_RELEVANCE_FIELDS].join(
      ',',
    ),
    order: applyRelevanceBoost
      ? 'total_boost.desc.nullslast,position.asc'
      : 'position.asc',
    limit: 5,
    ...getRegionApiQueryParams({ audienceRegions }),
  },
});

export const fetchTrendingEpisodesRequest = ({
  compliance,
  audienceRegions,
  applyRelevanceBoost,
}: EpisodeRequestBase) => ({
  url: PostgrestEndpoints.TRENDING_EPISODES,
  params: {
    region: `eq.${ComplianceTypeToApiQueryParam[compliance]}`,
    is_archived: 'is.false',
    select: [...EPISODE_CARD_REQUIRED_FIELDS, ...EPISODE_RELEVANCE_FIELDS].join(
      ',',
    ),
    order: applyRelevanceBoost
      ? 'total_boost.desc.nullslast,position.asc'
      : 'position.asc',
    ...getRegionApiQueryParams({ audienceRegions }),
  },
});

export const fetchNewEpisodesRequest = ({
  isTechnical,
  compliance,
  audienceRegions,
  limit = CAROUSEL_EPISODES_LIMIT,
  applyRelevanceBoost,
}: FetchNewEpisodesParams) => ({
  url: PostgrestEndpoints.EPISODE,
  params: {
    is_archived: 'is.false',
    select: [...EPISODE_CARD_REQUIRED_FIELDS, ...EPISODE_RELEVANCE_FIELDS].join(
      ',',
    ),
    region: `eq.${ComplianceTypeToApiQueryParam[compliance]}`,
    limit,
    order: applyRelevanceBoost
      ? 'total_boost.desc.nullslast,publish_date.desc'
      : 'publish_date.desc',
    ...(isTechnical === undefined
      ? {}
      : { is_technical: `is.${!!isTechnical}` }),
    ...getRegionApiQueryParams({ audienceRegions }),
  },
});

export const fetchOrganizationPlaylistEpisodes = ({
  compliance,
}: {
  compliance: ComplianceType;
}) => ({
  url: PostgrestEndpoints.ORGANIZATION_PLAYLIST,
  params: {
    region: `eq.${ComplianceTypeToApiQueryParam[compliance]}`,
    is_archived: 'is.false',
  },
});

export const fetchLibraryEpisodesRequest = ({
  librarySlugs,
  facets,
  params,
}: FetchLibraryEpisodesParams) => ({
  url: PostgrestEndpoints.LIBRARY_EPISODES,
  method: 'POST',
  body: {
    ...(librarySlugs ? { library_slugs: librarySlugs } : {}),
    episode_filter: getFacetFilters(facets),
  },
  params: {
    // select: EPISODE_CARD_REQUIRED_FIELDS.join(','),  // TODO: Can't use this without adjusting fields
    ...getSortByQuery(params?.sortBy as string),
    limit: params?.limit,
  },
});

export const fetchCategoryEpisodesRequest = ({
  libraryCategorySlug,
  compliance,
  audienceRegions,
  limit,
  applyRelevanceBoost,
}: FetchCategoryEpisodesParams) => ({
  url: PostgrestEndpoints.EPISODE,
  params: {
    is_archived: 'is.false',
    select: [...EPISODE_CARD_REQUIRED_FIELDS, ...EPISODE_RELEVANCE_FIELDS].join(
      ',',
    ),
    region: `eq.${ComplianceTypeToApiQueryParam[compliance]}`,
    library_categories: `cs.[{"slug":"${libraryCategorySlug}"}]`,
    order: applyRelevanceBoost
      ? 'total_boost.desc.nullslast,publish_date.desc'
      : 'publish_date.desc',
    ...(limit ? { limit } : {}),
    ...getRegionApiQueryParams({ audienceRegions }),
  },
});

export const fetchEpisodeRequest = ({
  episodeId,
  compliance,
  includeArchived = false,
}: FetchEpisodeParams) => ({
  url: PostgrestEndpoints.EPISODE,
  params: {
    episode_id: `eq.${episodeId}`,
    ...(compliance
      ? {
          region: `eq.${ComplianceTypeToApiQueryParam[compliance]}`,
        }
      : {}),
    ...(includeArchived ? {} : { is_archived: 'is.false' }),
  },
});

export const fetchEpisodesRequest = ({
  episodeIds,
  compliance,
  includeArchived = false,
  audienceRegions,
}: FetchEpisodesParams) => ({
  url: PostgrestEndpoints.EPISODE,
  params: {
    episode_id: `in.(${episodeIds.join(',')})`,
    select: EPISODE_CARD_REQUIRED_FIELDS.join(','),
    ...(compliance
      ? {
          region: `eq.${ComplianceTypeToApiQueryParam[compliance]}`,
        }
      : {}),
    ...(includeArchived ? {} : { is_archived: 'is.false' }),
    ...getRegionApiQueryParams({ audienceRegions }),
  },
});

export const fetchLibraryCategoriesRequest = ({
  facets,
}: {
  facets?: Record<string, string[]>;
} = {}) => ({
  url: PostgrestEndpoints.LIBRARY_CATEGORIES,
  method: 'POST',
  body: {
    ...getFacetFilters(facets),
  },
});

export const fetchEpisodeFacetsRequest = () => ({
  url: PostgrestEndpoints.EPISODE_FACETS,
});

export const fetchEpisodeCategoriesRequest = ({
  episodeIds,
}: {
  episodeIds: string[];
}) => ({
  url: PostgrestEndpoints.EPISODE_CATEGORIES,
  method: 'POST',
  body: {
    episode_ids: episodeIds,
  },
});

export const fetchFieldsOfStudyRequest = () => ({
  url: PostgrestEndpoints.FIELDS_OF_STUDY,
});

export const fetchLearningPathsRequest = ({
  compliance,
  learningPathIds,
}: {
  compliance: ComplianceType;
  learningPathIds?: number[];
}) => ({
  url: PostgrestEndpoints.LEARNING_PATHS,
  params: {
    region: `eq.${ComplianceTypeToApiQueryParam[compliance]}`,
    ...(learningPathIds
      ? { group_id: `in.(${learningPathIds.join(',')})` }
      : {}),
  },
});

export const fetchLearningPathRequest = ({
  compliance,
  learningPathId,
}: {
  compliance: ComplianceType;
  learningPathId: number;
}) => ({
  url: PostgrestEndpoints.LEARNING_PATHS,
  params: {
    region: `eq.${ComplianceTypeToApiQueryParam[compliance]}`,
    group_id: `eq.${learningPathId}`,
  },
});

export const fetchEpisodeLearningPathsRequest = ({
  episodeId,
}: {
  episodeId: string;
}) => ({
  url: PostgrestEndpoints.EPISODE_LEARNING_PATHS,
  params: {
    episode_id: `eq.${episodeId}`,
  },
});

export const reorderFeaturedLpsRequest = ({
  featuredLearningPathId,
  newOrderId,
}: {
  featuredLearningPathId: number;
  newOrderId: number;
}) => ({
  url: PostgrestEndpoints.REORDER_ORG_PINNED_LPS,
  method: 'POST',
  body: {
    featured_learning_path_id: featuredLearningPathId,
    new_order_id: newOrderId,
  },
});

export const fetchOrgPinnedLpsRequest = () => ({
  url: PostgrestEndpoints.ORG_PINNED_LPS,
});

export const pinLpRequest = ({ groupId }: { groupId: number }) => ({
  url: PostgrestEndpoints.PIN_LP,
  method: 'POST',
  body: {
    group_id: groupId,
  },
});

export const unpinLpRequest = ({ groupId }: { groupId: number }) => ({
  url: PostgrestEndpoints.UNPIN_LP,
  method: 'POST',
  body: {
    group_id: groupId,
  },
});

export const fetchChapterRequest = ({
  chapterId,
  compliance,
}: {
  chapterId: string;
  compliance: ComplianceType;
}) => {
  return {
    url: PostgrestEndpoints.CHAPTER,
    params: {
      firestore_chapter_id: `eq.${chapterId}`,
      region: `eq.${ComplianceTypeToApiQueryParam[compliance]}`,
    },
  };
};

export const fetchEpisodeQuizzesRequest = ({
  episodeId,
  compliance,
  testMode,
}: FetchEpisodeQuizzesParams) => ({
  url: PostgrestEndpoints.QUIZ,
  params: {
    episode_id: `eq.${episodeId}`,
    region: `eq.${ComplianceTypeToApiQueryParam[compliance]}`,
  },
  headers: testMode ? { 'x-test-mode': 'true' } : undefined,
});

export const fetchUserAssessmentRequest = ({
  episodeId,
  testMode,
}: FetchUserAssessmentParams) => ({
  method: 'POST',
  url: PostgrestEndpoints.USER_ASSESSMENT,
  body: {
    episode_id: episodeId,
    create_if_not_exists: true,
  },
  headers: testMode ? { 'x-test-mode': 'true' } : undefined,
});

export const fetchChapterContentRequest = ({
  episodeId,
}: {
  episodeId: string;
}) => ({
  url: PostgrestEndpoints.CHAPTER_URL,
  params: {
    episode_id: `eq.${episodeId}`,
  },
});

export const fetchTrailerContentRequest = ({
  episodeId,
}: {
  episodeId: string;
}) => ({
  url: PostgrestEndpoints.TRAILER_URL,
  params: {
    episode_id: `eq.${episodeId}`,
  },
});

export const fetchEpisodeChaptersRequest = ({
  episodeId,
  compliance,
}: {
  episodeId: string;
  compliance: ComplianceType;
}) => ({
  url: PostgrestEndpoints.CHAPTER,
  params: {
    episode_id: `eq.${episodeId}`,
    region: `eq.${ComplianceTypeToApiQueryParam[compliance]}`,
  },
});

export const fetchDesignationOptionsRequest = () => ({
  url: PostgrestEndpoints.DESIGNATION_OPTIONS,
});

export const sendAccountLinkingEmailRequest = ({
  email,
}: {
  email: string;
}) => ({
  url: PostgrestEndpoints.SEND_ACCOUNT_LINKING_EMAIL,
  method: 'POST',
  body: {
    email,
  },
});

export const updateUserDesignationsRequest = ({
  userDesignations,
}: {
  userDesignations: unknown[];
}) => ({
  url: PostgrestEndpoints.UPDATE_USER_DESIGNATIONS,
  method: 'POST',
  body: userDesignations,
});

export const finalizeRegistrationRequest = () => ({
  url: PostgrestEndpoints.FINALIZE_REGISTRATION,
  method: 'POST',
});

export const fetchPDHoursRequest = ({
  dateRange,
}: {
  dateRange: {
    fromDate?: string;
    toDate?: string;
  };
}) => ({
  url: PostgrestEndpoints.CREDITS,
  params: {
    order: 'date_completed.desc',
    ...(dateRange.fromDate &&
      dateRange.toDate && {
        and: `(date_completed.gte.${dateRange.fromDate},date_completed.lte.${dateRange.toDate})`,
      }),
    ...(dateRange.fromDate &&
      !dateRange.toDate && {
        date_completed: `gte.${dateRange.fromDate}`,
      }),
    ...(dateRange.toDate &&
      !dateRange.fromDate && {
        date_completed: `lte.${dateRange.toDate}`,
      }),
  },
});

export const fetchEpisodeCreditsRequest = ({
  episodeId,
}: {
  episodeId: string;
}) => ({
  url: PostgrestEndpoints.CREDITS,
  params: {
    episode_id: `eq.${episodeId}`,
  },
});

export const fetchCPACanadaEpisodeRequest = () => ({
  url: PostgrestEndpoints.CPA_CANADA_EPISODE,
});

export const getOffers = ({ skus }: { skus: string[] }) => ({
  url: PostgrestEndpoints.OFFERS,
  params: {
    sku: `in.(${skus.join(',')})`,
    active: 'is.true',
  },
});

export const fetchPrimaryRegion = () => ({
  url: PostgrestEndpoints.PRIMARY_REGION,
});

export const fetchPlayerConfig = () => ({
  url: PostgrestEndpoints.PLAYER_CONFIG,
});

export const fetchSession = ({ episodeId }: { episodeId: string }) => ({
  url: PostgrestEndpoints.FIND_PLAYER_SESSION,
  params: {
    episode_id: episodeId,
  },
});

export const resumeLastPlayerSession = () => ({
  url: PostgrestEndpoints.RESUME_LAST_PLAYER_SESSION,
  method: 'POST',
});

export const startPlayerSession = ({ episodeId }: { episodeId: string }) => ({
  url: PostgrestEndpoints.START_PLAYER_SESSION,
  method: 'POST',
  body: {
    episode_id: episodeId,
  },
});

export const playPlayerSession = ({
  sessionId,
  chapterId,
  position,
}: PlaySessionParams) => ({
  url: PostgrestEndpoints.PLAY_PLAYER_SESSION,
  method: 'POST',
  body: {
    session_id: sessionId,
    chapter_id: chapterId,
    position: position && Math.floor(position),
    verbose: true,
  },
});

export const pausePlayerSession = ({
  sessionId,
  chapterId,
  position,
}: PauseSessionParams) => ({
  url: PostgrestEndpoints.PAUSE_PLAYER_SESSION,
  method: 'POST',
  body: {
    session_id: sessionId,
    chapter_id: chapterId,
    position: Math.floor(position),
    verbose: true,
  },
});

export const setPosition = ({
  sessionId,
  chapterId,
  position,
}: PostSetPositionParams) => ({
  url: PostgrestEndpoints.SET_SESSION_POSITION,
  method: 'POST',
  body: {
    session_id: sessionId,
    chapter_id: chapterId,
    position: Math.floor(position),
    client_type: 'web',
    verbose: true,
  },
});

export const seekPosition = ({
  sessionId,
  chapterId,
  position,
  isInTestMode,
}: PostSeekPositionParams) => ({
  url: PostgrestEndpoints.SEEK_SESSION_POSITION,
  method: 'POST',
  body: {
    session_id: sessionId,
    chapter_id: chapterId,
    position: Math.floor(position),
    client_type: 'web',
    verbose: true,
    test_mode: isInTestMode,
  },
});

export const fetchPlayerSessions = () => ({
  url: PostgrestEndpoints.PLAYER_SESSIONS,
});

export const answerQuiz = ({
  sessionId,
  chapterId,
  quizId,
  answerId,
}: AnswerQuizPayload) => ({
  url: PostgrestEndpoints.ANSWER_QUIZ,
  method: 'POST',
  body: {
    session_id: sessionId,
    chapter_id: chapterId,
    quiz_id: quizId,
    answer_id: Number(answerId),
  },
});

export const submitUserAssessment = ({
  episodeId,
  answers,
  sessionId,
}: SubmitUserAssessmentPayload) => ({
  url: PostgrestEndpoints.SUBMIT_USER_ASSESSMENT,
  method: 'POST',
  body: {
    episode_id: episodeId,
    assessment_answer_ids: answers,
    session_id: sessionId,
  },
});

export const claimPlayerSession = ({
  sessionId,
}: ClaimPlayerSessionPayload) => ({
  url: PostgrestEndpoints.CLAIM_PLAYER_SESSION,
  method: 'POST',
  body: {
    session_id: sessionId,
  },
});

export const submitProductFeedback = ({
  responses,
  isDismissed = false,
  metadata = {},
}: SubmitProductFeedbackPayload) => ({
  url: PostgrestEndpoints.SUBMIT_PRODUCT_FEEDBACK,
  method: 'POST',
  body: {
    responses,
    isDismissed,
    metadata: {
      ...metadata,
      source: 'web',
    },
  },
});

export const fetchRecommendedEpisodesRequest = ({
  compliance,
  audienceRegions,
}: EpisodeRequestBase) => ({
  url: PostgrestEndpoints.RECOMMENDED_EPISODES,
  method: 'GET',
  params: {
    limit: 10,
    region: `eq.${ComplianceTypeToApiQueryParam[compliance]}`,
    select: EPISODE_CARD_REQUIRED_FIELDS.join(','),
    ...getRegionApiQueryParams({ audienceRegions }),
  },
});

export const fetchRelatedEpisodesRequest = ({
  compliance,
  episodeId,
  audienceRegions,
}: {
  episodeId: string;
} & EpisodeRequestBase) => ({
  url: PostgrestEndpoints.RELATED_EPISODES,
  method: 'GET',
  params: {
    current_episode_id: `eq.${episodeId}`,
    region: `eq.${ComplianceTypeToApiQueryParam[compliance]}`,
    select: EPISODE_CARD_REQUIRED_FIELDS.join(','),
    limit: 6,
    ...getRegionApiQueryParams({ audienceRegions }),
  },
});

export const updateOrgRecommendedEpisodesRequest = ({
  episodes,
}: {
  episodes: { episodeId: string; position: number }[];
}) => ({
  url: PostgrestEndpoints.UPDATE_ORG_RECOMMENDED_EPISODES,
  method: 'POST',
  body: { episodes: episodes.map(snakeCaseKeys) },
});

export const fetchReportingConfig = () => ({
  url: PostgrestEndpoints.REPORTING_CONFIG,
});

export const fetchListenerDistributionReport = ({
  dateRange,
}: {
  dateRange: Partial<DateRange>;
}) => ({
  url: PostgrestEndpoints.LISTENER_DISTRIBUTION_REPORT,
  params: convertToPostgrestDateRange(dateRange),
});

export const fetchEngagementTrendReport = ({
  dateRange,
}: {
  dateRange: Partial<DateRange>;
}) => ({
  url: PostgrestEndpoints.ENGAGEMENT_TREND_REPORT,
  params: convertToPostgrestDateRange(dateRange),
});

export const fetchEngagementSummaryReport = ({
  dateRange,
}: {
  dateRange: Partial<DateRange>;
}) => ({
  url: PostgrestEndpoints.ENGAGEMENT_SUMMARY_REPORT,
  params: convertToPostgrestDateRange(dateRange),
});

export const fetchTopListenersReport = ({
  dateRange,
}: {
  dateRange: Partial<DateRange>;
}) => ({
  url: PostgrestEndpoints.TOP_LISTENERS_REPORT,
  params: convertToPostgrestDateRange(dateRange),
});

export const fetchTopEpisodesReport = ({
  dateRange,
}: {
  dateRange: Partial<DateRange>;
}) => ({
  url: PostgrestEndpoints.TOP_EPISODES_REPORT,
  params: convertToPostgrestDateRange(dateRange),
});

export const fetchOrganizationMembershipSummary = () => ({
  url: PostgrestEndpoints.ORGANIZATION_MEMBERSHIP_SUMMARY,
});

export const fetchOrganizationMembersCount = () => ({
  url: PostgrestEndpoints.ORGANIZATION_MEMBERS,
  method: 'GET',
  params: {
    limit: 1,
  },
  headers: {
    Prefer: 'count=exact',
  },
});

export const fetchOrganizationMembersPaginated = ({
  limit,
  offset,
}: {
  limit: number;
  offset: number;
}) => ({
  url: PostgrestEndpoints.ORGANIZATION_MEMBERS,
  method: 'GET',
  params: {
    limit,
    offset,
  },
});

export const listenerDetailReportCsv = ({
  dateRange,
}: {
  dateRange: Partial<DateRange>;
}) => ({
  url: PostgrestEndpoints.LISTENER_DETAIL_REPORT,
  method: 'POST',
  body: convertToPostgrestDateRange(dateRange),
  headers: {
    accept: 'text/csv',
  },
  responseHandler: (response: Response) => response.blob(),
});

export const listenerSummaryReportCsv = ({
  dateRange,
}: {
  dateRange: Partial<DateRange>;
}) => ({
  url: PostgrestEndpoints.LISTENER_SUMMARY_REPORT,
  method: 'POST',
  body: convertToPostgrestDateRange(dateRange),
  headers: {
    accept: 'text/csv',
  },
  responseHandler: (response: Response) => response.blob(),
});

export const fetchPlanLimits = () => ({
  url: PostgrestEndpoints.PLAN_LIMITS,
  method: 'GET',
});

export const reInviteOrganizationMembers = (
  organizationMemberIds: number[],
) => ({
  url: PostgrestEndpoints.REINVITE_ORGANIZATION_MEMBERS,
  method: 'POST',
  body: {
    organization_member_ids: organizationMemberIds,
  },
});

export const archiveOrganizationMembers = (
  organizationMemberIds: number[],
) => ({
  url: PostgrestEndpoints.ARCHIVE_ORGANIZATION_MEMBERS,
  method: 'POST',
  body: {
    organization_member_ids: organizationMemberIds,
  },
});

export const addOrganizationMembers = ({
  sendInvite,
  newMembers,
}: AddMembersPayload) => ({
  url: PostgrestEndpoints.ADD_ORGANIZATION_MEMBERS,
  method: 'POST',
  body: {
    send_invite: sendInvite,
    new_members: newMembers,
  },
});

export const editOrganizationMember = (payload: EditMembersPayload) => ({
  url: PostgrestEndpoints.EDIT_ORGANIZATION_MEMBERS,
  method: 'POST',
  body: payload.map(member => ({
    organization_member_id: member.organizationMemberId,
    email: member.email,
    name: member.name,
  })),
});

export const fetchOrganizationSettings = () => ({
  url: PostgrestEndpoints.ORGANIZATION_SETTING,
});

export const fetchListenerData = (props?: ListenerDataProps) => ({
  url: PostgrestEndpoints.LISTENER_DATA,
  params: {
    episode_id: props?.episodeIds
      ? `in.(${props.episodeIds.join(',')})`
      : undefined,
  },
});

export const createNewTeam = ({ companyName }: { companyName: string }) => ({
  url: PostgrestEndpoints.CREATE_NEW_TEAM,
  method: 'POST',
  body: { organization_name: companyName },
});

export const fetchEpisodesScheduledForArchiving = ({
  episodeId,
}: {
  episodeId: string;
}) => ({
  url: PostgrestEndpoints.EPISODES_SCHEDULED_FOR_ARCHIVING,
  params: {
    episode_id: `eq.${episodeId}`,
  },
});

export const bookmarkSession = ({
  sessionId,
  bookmarkedAt,
}: {
  sessionId: string;
  bookmarkedAt: string;
}) => ({
  url: PostgrestEndpoints.BOOKMARK_SESSION,
  method: 'POST',
  body: {
    session_id: sessionId,
    bookmarked_at: bookmarkedAt,
  },
});

export const unbookmarkSession = ({ sessionId }: { sessionId: string }) => ({
  url: PostgrestEndpoints.UNBOOKMARK_SESSION,
  method: 'POST',
  body: {
    session_id: sessionId,
  },
});

export const fetchUserStripeCustomerInfo = () => ({
  url: PostgrestEndpoints.STRIPE_CUSTOMER_INFO,
  method: 'GET',
});

export const generateBillingPortalLink = ({
  redirectUrl,
}: {
  redirectUrl?: string;
}) => ({
  url: PostgrestEndpoints.GET_BILLING_PORTAL_LINK,
  method: 'POST',
  body: {
    redirect_url: redirectUrl,
  },
});

export const fetchPricingTableValues = ({
  region,
  metadata,
}: {
  region: Region;
  metadata?: Record<string, string>;
}) => ({
  url: PostgrestEndpoints.ANONYMOUS_PRICING_TABLE_VALUES,
  method: 'POST',
  body: {
    country_code: region,
    stripe_environment_id: config.STRIPE_ENVIRONMENT_ID,
    ...(metadata && { metadata }),
  },
});

export const fetchDefaultPromoCode = ({ region }: { region: Region }) => ({
  url: PostgrestEndpoints.DEFAULT_PROMO_CODE,
  method: 'POST',
  body: {
    country_code: region,
    stripe_environment_id: config.STRIPE_ENVIRONMENT_ID,
  },
});

export const fetchPurchaseFeedbackOptions = () => ({
  url: PostgrestEndpoints.PURCHASE_FEEDBACK_OPTIONS,
  method: 'GET',
});

export const submitPurchaseFeedback = ({
  howdyhauOptionId,
  detailResponse,
  checkoutSessionId,
}: {
  howdyhauOptionId: string;
  detailResponse: string;
  checkoutSessionId: string;
}) => ({
  url: PostgrestEndpoints.SUBMIT_PURCHASE_FEEDBACK,
  method: 'POST',
  body: {
    howdyhau_option_id: howdyhauOptionId,
    detail: detailResponse,
    checkout_session_id: checkoutSessionId,
    metadata: {
      source: 'web',
    },
  },
});

export const cancelStripeSubscription = ({
  comment,
  feedback,
}: CancelStripeSubscriptionPayload) => ({
  url: PostgrestEndpoints.STRIPE_CANCEL_SUBSCRIPTION,
  method: 'POST',
  body: {
    comment,
    feedback,
  },
});

export const submitSessionFeedback = ({
  sessionId,
  response,
  submittedAt,
}: {
  sessionId: string;
  response: EpisodeFeedback;
  submittedAt: string;
}) => ({
  url: PostgrestEndpoints.SUBMIT_EPISODE_FEEDBACK,
  method: 'POST',
  body: {
    session_id: sessionId,
    response: {
      episode_rating: response.episodeRating,
      rating_reason: response.ratingReason,
      text_feedback: response.textFeedback,
    },
    submitted_at: submittedAt,
  },
});

export const submitEpisodeEvaluation = ({
  sessionId,
  response,
}: {
  sessionId: string;
  response: Array<{
    id: string;
    question: string;
    answer: number;
    type: string;
  }>;
}) => ({
  url: PostgrestEndpoints.SUBMIT_EPISODE_EVALUATION,
  method: 'POST',
  body: {
    session_id: sessionId,
    response,
  },
});

export const fetchTriennialSummaryRequest = () => ({
  url: PostgrestEndpoints.TRIENNIAL_SUMMARY,
  method: 'POST',
});

export const fetchUserUIStates = () => ({
  url: PostgrestEndpoints.GET_USER_UI_STATES,
  method: 'POST',
});

export const updateUserUIState = ({
  key,
  value,
}: {
  key: string;
  value: any;
}) => ({
  url: PostgrestEndpoints.UPDATE_USER_UI_STATE,
  method: 'POST',
  body: { key, value },
});

export const fetchContentPreferences = () => ({
  url: PostgrestEndpoints.CONTENT_PREFERENCES,
  method: 'POST',
});

export const setRegionPreference = ({
  regionSlugs,
}: {
  regionSlugs: string[];
}) => ({
  url: PostgrestEndpoints.SET_REGION_PREFERENCE,
  method: 'POST',
  body: {
    region_slugs: regionSlugs,
  },
});
